// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material"
import { FC, useContext, useEffect, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomController } from '../common/CustomController'
import { CustomModal } from "../common/CustomModal"
// others
import { UpdateCourtInput, useUpdateCourtMutation } from '../../../generated'
import { COURT_EDIT_FIELDS, GENERAL_INFORMATION } from "../../constants"
import { AppContext, AuthContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { DispatchAndState, ErrorException } from "../../interfaceTypes"
import { titleize } from "../../utils"
import { createCourtValidationSchema } from '../../validationSchema'
import { CourtAction, CourtActionType, CourtState } from "./CourtReducer"

export const ViewCurrentCourt: FC<DispatchAndState<CourtState, CourtAction>> = ({ state, dispatch, refetch }) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const { isEdit, dispatch: appDispatch } = useContext(AppContext)
  const { isAdmin } = useContext(AuthContext)
  const { currentCourt } = state || {}
  const { id, name, county } = currentCourt || {}

  const handleClose = () => {
    appDispatch({ type: ActionType.SET_IS_EDIT, isEdit: false })
    dispatch({ type: CourtActionType.SET_CURRENT_COURT, currentCourt: null })
    setIsEditOpen(false)
    reset()
  }

  const methods = useForm<UpdateCourtInput>({
    mode: 'all',
    resolver: yupResolver(createCourtValidationSchema),

    defaultValues: {
      name: titleize(name || ''),
      county
    }
  })

  const { handleSubmit, reset, setValue } = methods;

  const [updateCourtMutation, { loading: updateCourtLoading }] = useUpdateCourtMutation({
    onError(error) {
      const { graphQLErrors } = error

      if (graphQLErrors) {
        graphQLErrors.map(
          // eslint-disable-next-line
          ({ extensions }) => {
            if (extensions) {
              const { exception } = extensions;
              if (exception) {
                const { response } = exception as ErrorException
                const { response: errorResponse } = response || {}
                if (errorResponse) {
                  const { error } = errorResponse
                  Alert.error(error)
                }
              }
            }
          })
      }
    },

    onCompleted(data) {
      if (data) {
        const { updateCourt: { response, court } } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && court) {
            refetch && refetch()
            Alert.success('Court updated successfully')
            dispatch({ type: CourtActionType.SET_CURRENT_COURT, currentCourt: court })
            resetFields()
            setIsEditOpen(false);
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<UpdateCourtInput> = async (data: UpdateCourtInput) => {
    const { name, county } = data

    if (id) {
      await updateCourtMutation({
        variables: {
          updateCourtInput: {
            name,
            county,
            id
          }
        }
      })
    }
  }

  useEffect(() => {
    name && setValue("name", titleize(name));
    county && setValue("county", titleize(county));

    // eslint-disable-next-line
  }, [name, county]);

  const resetFields = () => {
    reset({
      name: name || "",
      county: county || ""
    });
  }

  return (
    <CustomModal title="Court detail" isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems='center'>
            <Typography variant="body2" fontSize='18px' fontWeight='medium'>
              {GENERAL_INFORMATION}
            </Typography>

            {isAdmin &&
              <Box>
                <IconButton color="primary" onClick={() => setIsEditOpen(!isEditOpen)} aria-label={isEditOpen ? 'back' : 'edit'}>
                  {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
                </IconButton>

                {isEditOpen &&
                  <IconButton color="primary" type='submit' disabled={updateCourtLoading}>
                    <SaveIcon />
                  </IconButton>
                }
              </Box>
            }
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <Grid item md={6} sm={12} xs={12}>
                  <Typography variant="body2" fontSize='14px'>Court Name:</Typography>
                  <Typography variant="h6" fontSize='16px' fontWeight='normal' textTransform='capitalize'>
                    {titleize(name || '')}
                  </Typography>
                </Grid>

                <Grid item md={6} sm={12} xs={12}>
                  <Typography variant='body2' fontSize='14px'>County:</Typography>
                  <Typography fontSize='16px' variant='h6' fontWeight='normal' textTransform='capitalize'>
                    {titleize(county || '--')}
                  </Typography>
                </Grid>

              </Grid>
            </Collapse>

            <Collapse in={isEditOpen}>
              <Grid container spacing={2} rowSpacing={0.5}>
                {COURT_EDIT_FIELDS.map((field, index) => {
                  const { fieldType, name, title } = field || {}

                  return (
                    <Grid item md={6} sm={12} xs={12} key={index}>
                      <CustomController
                        controllerName={name}
                        controllerLabel={title}
                        fieldType={fieldType}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            </Collapse>
          </Box>
        </form>
      </FormProvider>
    </CustomModal>
  )
}